import { wrapInColumn, wrapInRow, cleanupEmptyContainers, ensureContainerInvariants } from './containers';
import { mapTableCellLists } from './table';
import { findBlock, findTableIdForBlock, removeBlockFromTree } from './tree';

/**
 * Insert a block relative to a target.
 *  - top/bottom: splices into whatever list holds the target (top level,
 *    a column's stack, a callout's children, or a table cell) → vertical.
 *  - left/right: creates columns. On a lone block in a column, adds a
 *    sibling column to the row; on a block inside a stacked column, nests
 *    a new row inside the column at that block → nested columns.
 *    Inside a table cell, left/right become top/bottom (no columns in cells).
 */
export const insertBlock = (blocks, newBlock, targetId, position) => {
  const isHorizontal = position === 'left' || position === 'right';

  const mapList = (list, parentType) => {
    // Cells can't host columns — a sideways drop becomes a sibling above/below.
    const effective =
      parentType === 'table-cell' && isHorizontal
        ? position === 'left'
          ? 'top'
          : 'bottom'
        : position;

    const idx = list.findIndex((b) => b.id === targetId);
    if (idx !== -1) {
      const next = [...list];
      const target = next[idx];
      if (effective === 'top') {
        next.splice(idx, 0, newBlock);
      } else if (effective === 'bottom') {
        next.splice(idx + 1, 0, newBlock);
      } else if (effective === 'left' || effective === 'right') {
        // Split at this block: wrap target + new block into a row of columns.
        // Inside an existing column this nests a row (nested columns).
        next[idx] =
          effective === 'left' ? wrapInRow(newBlock, target) : wrapInRow(target, newBlock);
      }
      return next;
    }

    let changed = false;
    const next = list.map((b) => {
      // Sibling-column shortcut: dropping left/right on a block that is the
      // ONLY block of a column adds a new column to that row (rather than
      // pointlessly nesting a row inside the column).
      if (b.type === 'row' && isHorizontal) {
        const colIdx = (b.children || []).findIndex(
          (col) =>
            col.type === 'column' && col.children?.length === 1 && col.children[0].id === targetId
        );
        if (colIdx !== -1) {
          changed = true;
          const columns = [...b.children];
          columns.splice(position === 'left' ? colIdx : colIdx + 1, 0, wrapInColumn(newBlock));
          return { ...b, children: columns };
        }
      }
      let current = b;
      if (b.children?.length) {
        const children = mapList(b.children, b.type);
        if (children !== b.children) {
          changed = true;
          current = { ...current, children };
        }
      }
      if (current.type === 'table') {
        const mapped = mapTableCellLists(current, (cell) => mapList(cell, 'table-cell'));
        if (mapped !== current) {
          changed = true;
          current = mapped;
        }
      }
      return current;
    });
    return changed ? next : list;
  };

  return mapList(blocks, null);
};

// Types that need the page drag surface — land beside the host table instead.
const CELL_DROP_EXCLUDE = new Set([
  'table',
  'row',
  'variables',
  'callout',
  'quote',
  'toggle',
  'choice',
  'snippet',
]);

const isEmptyTextBlock = (b) =>
  b?.type === 'text' && !String(b.content || '').replace(/<[^>]*>/g, '').trim();

/** Dropping into a placeholder cell should replace the empty text, not stack. */
const tidyEmptyCellPlaceholders = (blocks) => {
  let changed = false;
  const next = blocks.map((block) => {
    let current = block;
    if (block.children?.length) {
      const children = tidyEmptyCellPlaceholders(block.children);
      if (children !== block.children) {
        changed = true;
        current = { ...current, children };
      }
    }
    if (current.type === 'table') {
      const mapped = mapTableCellLists(current, (cell) => {
        if (!Array.isArray(cell) || cell.length < 2) return cell;
        const kept = cell.filter((b) => !isEmptyTextBlock(b));
        return kept.length && kept.length !== cell.length ? kept : cell;
      });
      if (mapped !== current) {
        changed = true;
        current = mapped;
      }
    }
    return current;
  });
  return changed ? next : blocks;
};

// Move a block: remove from its old spot, insert at the new one, tidy up
export const handleBlockDrop = (blocks, draggedId, targetId, dropZone) => {
  if (!dropZone || draggedId === targetId) return blocks;
  const draggedBlock = findBlock(blocks, draggedId);
  if (!draggedBlock) return blocks;

  let insertTargetId = targetId;
  const hostTableId = findTableIdForBlock(blocks, targetId);
  if (hostTableId && draggedBlock.id === hostTableId) return blocks;
  if (hostTableId && CELL_DROP_EXCLUDE.has(draggedBlock.type)) {
    insertTargetId = hostTableId;
  }

  let next = removeBlockFromTree(blocks, draggedId);
  next = insertBlock(next, draggedBlock, insertTargetId, dropZone);
  next = tidyEmptyCellPlaceholders(next);
  next = cleanupEmptyContainers(next);
  // Dragging a callout's last child out must not leave a dead callout
  return ensureContainerInvariants(next);
};
